import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * OptimizedImage - A component that provides lazy loading and WebP support
 * 
 * Features:
 * - Lazy loading with Intersection Observer
 * - Blur placeholder while loading
 * - Error handling with fallback
 * - Responsive sizing
 */
const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  width,
  height,
  placeholderColor = '#e2e8f0',
  loading = 'lazy',
  onLoad,
  onError,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  const markAsInView = useCallback(() => {
    setIsInView(true);
  }, []);

  useEffect(() => {
    const currentRef = imgRef.current;
    if (!currentRef) return;
    
    // Use native lazy loading if available, otherwise use Intersection Observer
    if ('loading' in HTMLImageElement.prototype) {
      markAsInView();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          markAsInView();
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.01
      }
    );

    observer.observe(currentRef);
    return () => observer.disconnect();
  }, [markAsInView]);

  const handleLoad = useCallback((e) => {
    setIsLoaded(true);
    onLoad?.(e);
  }, [onLoad]);

  const handleError = useCallback((e) => {
    setHasError(true);
    onError?.(e);
  }, [onError]);

  // Generate WebP source if the browser supports it
  const getWebPSource = (originalSrc) => {
    if (!originalSrc) return null;
    if (originalSrc.includes('.webp') || originalSrc.startsWith('data:')) {
      return null;
    }
    if (originalSrc.includes('googleusercontent.com') || originalSrc.includes('unsplash.com')) {
      return null;
    }
    return originalSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  };

  const webpSrc = getWebPSource(src);

  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={{ 
        backgroundColor: placeholderColor,
        width: width ? `${width}px` : undefined,
        height: height ? `${height}px` : undefined,
      }}
    >
      {/* Blur placeholder */}
      {!isLoaded && !hasError && (
        <div 
          className="absolute inset-0 animate-pulse"
          style={{ backgroundColor: placeholderColor }}
        />
      )}
      
      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <span className="text-gray-400 text-sm">Failed to load</span>
        </div>
      )}
      
      {/* Actual image with picture element for WebP support */}
      {isInView && !hasError && (
        <picture>
          {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
          <img
            src={src}
            alt={alt}
            loading={loading}
            onLoad={handleLoad}
            onError={handleError}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            {...props}
          />
        </picture>
      )}
    </div>
  );
};

export default OptimizedImage;
